import React from 'react';

interface SummaryTabProps {
  reportData?: any; // We'll properly type this once we know the full structure
}

const SummaryTab: React.FC<SummaryTabProps> = ({ reportData }) => {
  // Default data in case no report data is provided
  const patientInfo = reportData?.patient_info || {
    patient_name: "Patient, Name",
    patient_id: "N/A",
    patient_gender: "Male",
    patient_dob: "10/1/1964",
    patient_age: "60 years"
  };

  const providerInfo = reportData?.metadata?.provider || "Provider, Name";

  const panelInfo = reportData?.panel_info || {
    panel_id: "169698",
    panel_type: "UTI"
  };

  const collectionInfo = reportData?.collection_info || {
    collection_date: "1/17/2025 9:42 AM"
  };

  const resultDate = reportData?.metadata?.result_date || "5/5/2025 1:08 AM";

  const pathogens = reportData?.analysis_results?.pathogens?.detected?.bacteria || [
    { name: "Escherichia coli", result: "Detected" }
  ];

  const resistanceGenes = reportData?.analysis_results?.pathogens?.resistance_by_class || {
    esbl: ["CTX-M"]
  };

  const complexityScore = reportData?.analysis_results?.complexity?.score || 2.11;
  const complexityDescription = reportData?.analysis_results?.complexity?.description || 
    "Standard case with common pathogens and clear treatment path";

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="border border-border rounded-md mb-6">
          <div className="bg-section-light p-4">
            <h3 className="text-lg font-semibold text-text">Patient Information</h3>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2">
            <div>
              <p><span className="font-medium">Name:</span> {patientInfo.patient_name}</p>
              <p><span className="font-medium">ID:</span> {patientInfo.patient_id}</p>
              <p><span className="font-medium">Gender:</span> {patientInfo.patient_gender}</p>
            </div>
            <div>
              <p><span className="font-medium">DOB:</span> {patientInfo.patient_dob}</p>
              <p><span className="font-medium">Age:</span> {patientInfo.patient_age}</p>
              <p><span className="font-medium">Provider:</span> {providerInfo}</p>
            </div>
          </div>
        </div>

        <div className="border border-border rounded-md mb-6">
          <div className="bg-section-light p-4">
            <h3 className="text-lg font-semibold text-text">Panel Information</h3>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            <div>
              <p><span className="font-medium">Panel ID:</span> {panelInfo.panel_id}</p>
              <p><span className="font-medium">Panel Type:</span> {panelInfo.panel_type}</p>
            </div>
            <div>
              <p><span className="font-medium">Collection Date:</span> {collectionInfo.collection_date}</p>
              <p><span className="font-medium">Result Date:</span> {resultDate}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="border border-border rounded-md">
          <div className="bg-section-light p-4">
            <h3 className="text-lg font-semibold text-text">Detected Organisms</h3>
          </div>
          <div className="p-4">
            <p><span className="font-medium">Total Detected:</span> {pathogens.length}</p>
            <p><span className="font-medium">Bacteria:</span> {pathogens.length}</p>
            <ul className="list-disc ml-5 mt-2">
              {pathogens.map((pathogen: any, index: number) => (
                <li key={index}>{pathogen.name} ({pathogen.result})</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border border-border rounded-md">
          <div className="bg-section-light p-4">
            <h3 className="text-lg font-semibold text-text">Resistance Profile</h3>
          </div>
          <div className="p-4">
            <p><span className="font-medium">Total Resistance Genes:</span> {
              Object.values(resistanceGenes).reduce((acc: number, genes: any) => acc + genes.length, 0)
            }</p>
            {Object.entries(resistanceGenes).map(([category, genes]: [string, any], index: number) => (
              <React.Fragment key={index}>
                <p><span className="font-medium">{category.toUpperCase()}:</span> {genes.length}</p>
                <ul className="list-disc ml-5 mt-2">
                  {genes.map((gene: string, geneIndex: number) => (
                    <li key={geneIndex}>{gene} (Count: 1)</li>
                  ))}
                </ul>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="border border-border rounded-md">
          <div className="bg-section-light p-4">
            <h3 className="text-lg font-semibold text-text">Complexity Assessment</h3>
          </div>
          <div className="p-4">
            <h4 className="font-medium mb-2">Case Complexity Assessment</h4>
            <p className="mb-2"><span className="font-medium">{
              complexityScore < 3 ? "Low Complexity" : 
              complexityScore < 7 ? "Moderate Complexity" : "High Complexity"
            }</span> {complexityScore.toFixed(2)}/10</p>
            
            <div className="relative h-24 w-full mb-4">
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-24 h-24" viewBox="0 0 120 120">
                  <path 
                    d="M10,60 A50,50 0 0,1 110,60" 
                    fill="none" 
                    stroke="#e0e0e0" 
                    strokeWidth="10"
                    strokeLinecap="round" 
                  />
                  <path 
                    d="M10,60 A50,50 0 0,1 30,15" 
                    fill="none" 
                    stroke="#945060" 
                    strokeWidth="10"
                    strokeLinecap="round" 
                  />
                  <circle cx="30" cy="15" r="6" fill="#4a4a4a" />
                  <text x="60" y="70" textAnchor="middle" fontSize="16" fill="#4a4a4a" fontWeight="bold">{complexityScore.toFixed(1)}</text>
                </svg>
              </div>
            </div>

            <p className="text-sm">{complexityDescription}</p>
            
            <h4 className="font-medium mt-4 mb-2">Factors:</h4>
            <ul className="list-disc ml-5">
              <li>Pathogen complexity</li>
              <li>Resistance patterns</li>
            </ul>
            
            <h4 className="font-medium mt-4 mb-2">Implications:</h4>
            <ul className="list-disc ml-5">
              <li>Treatment selection</li>
              <li>Monitoring requirements</li>
            </ul>
          </div>
        </div>

        <div className="border border-border rounded-md">
          <div className="bg-section-light p-4">
            <h3 className="text-lg font-semibold text-text">Primary Treatment</h3>
          </div>
          <div className="p-4">
            <h4 className="font-medium mb-2">
              {reportData?.analysis_results?.treatment?.primary?.drug_name || "Meropenem"}
            </h4>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <p><span className="font-medium">Dosage:</span> {reportData?.analysis_results?.treatment?.primary?.dosage || "1-2 gm"}</p>
              <p><span className="font-medium">Route:</span> {reportData?.analysis_results?.treatment?.primary?.route || "IV"}</p>
              <p><span className="font-medium">Frequency:</span> {reportData?.analysis_results?.treatment?.primary?.frequency || "q8h"}</p>
              <p><span className="font-medium">Duration:</span> 7-10 days</p>
            </div>
            
            <h4 className="font-medium mt-4 mb-2">
              {reportData?.analysis_results?.treatment?.primary?.drug_name || "Meropenem"}
            </h4>
            <p className="mb-2">Low-Medium Risk</p>
            
            <div className="relative h-24 w-full mb-4">
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-24 h-24" viewBox="0 0 120 120">
                  <path 
                    d="M10,60 A50,50 0 0,1 110,60" 
                    fill="none" 
                    stroke="#e0e0e0" 
                    strokeWidth="10"
                    strokeLinecap="round" 
                  />
                  <path 
                    d="M10,60 A50,50 0 0,1 50,20" 
                    fill="none" 
                    stroke="#8fb9aa" 
                    strokeWidth="10"
                    strokeLinecap="round" 
                  />
                  <circle cx="50" cy="20" r="6" fill="#4a4a4a" />
                  <text x="60" y="70" textAnchor="middle" fontSize="16" fill="#4a4a4a" fontWeight="bold">
                    {(reportData?.analysis_results?.treatment?.primary?.metadata?.adverse_score * 100).toFixed(0) || "36"}%
                  </text>
                </svg>
              </div>
            </div>
            
            <h4 className="font-medium mt-4 mb-2">Common Adverse Reactions:</h4>
            <ul className="list-disc ml-5">
              {(reportData?.analysis_results?.treatment?.primary?.metadata?.adverse_reactions || [
                {reaction: "Anaemia"},
                {reaction: "Diarrhea"},
                {reaction: "Nausea"}
              ]).slice(0, 3).map((reaction: any, index: number) => (
                <li key={index}>{reaction.reaction}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryTab; 